import React, { useEffect, useRef, useState } from 'react';
import { add, endOfDay, format, startOfDay } from 'date-fns';

import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { OverlayPanel } from 'primereact/overlaypanel';
import { classNames } from 'primereact/utils';
import { Nullable } from 'primereact/ts-helpers';

import styles from './CalendarRange.module.css';

export enum DefaultRangeSelectType {
  CurrentMonth,
  PastWeek,
  Past2Weeks,
  Past30Days,
}
export type RangeSelectOption = {
  label: string;
  startDate: Date;
  endDate: Date;
};

const normalizeRange = (value: Nullable<(Date | null)[]>): Date[] | undefined => {
  let range = !value ? undefined : Array.isArray(value) ? value : [value];
  if (range) {
    range = range.filter((d) => d);
    range.forEach((d, i) => {
      if (i === 0) {
        range![i] = startOfDay(d as Date);
      } else if (i === 1) {
        range![i] = endOfDay(d as Date);
      }
    });
  }
  return range as Date[] | undefined;
};

const CalendarRange = (props: {
  className?: string;
  label?: string;
  rangeSelectOptions: (DefaultRangeSelectType | RangeSelectOption)[];
  value: Date[] | undefined;
  onValueChange: (dateRange: Date[] | undefined) => void;
  /** When true, only commit a range once both dates (or a preset) are chosen. Partial dismiss reverts. */
  commitOnCompleteRange?: boolean;
}) => {
  const commitOnCompleteRange = props.commitOnCompleteRange ?? false;
  const op = useRef<OverlayPanel>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const suppressClickRef = useRef(false);
  const [draftRange, setDraftRange] = useState<Date[] | undefined>(props.value);
  const committedRangeRef = useRef<Date[] | undefined>(props.value);

  useEffect(() => {
    committedRangeRef.current = props.value;
    if (isOverlayHidden()) {
      setDraftRange(props.value);
    }
  }, [props.value]);

  const options: RangeSelectOption[] = props.rangeSelectOptions.map((option) => {
    const now = endOfDay(new Date());
    if ((option as DefaultRangeSelectType) in DefaultRangeSelectType) {
      switch (option) {
        case DefaultRangeSelectType.CurrentMonth: {
          const start = new Date(now.getFullYear(), now.getMonth(), 1);
          const end = endOfDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
          return {
            label: 'Current Month',
            startDate: start,
            endDate: end,
          };
        }
        case DefaultRangeSelectType.PastWeek:
          return {
            label: 'Past Week',
            startDate: startOfDay(add(now, { days: -7 })),
            endDate: now,
          };
        case DefaultRangeSelectType.Past2Weeks:
          return {
            label: 'Past 2 Weeks',
            startDate: startOfDay(add(now, { days: -14 })),
            endDate: now,
          };
        case DefaultRangeSelectType.Past30Days:
          return {
            label: 'Past 30 Days',
            startDate: startOfDay(add(now, { days: -30 })),
            endDate: now,
          };
        default:
          throw new Error(`Missing date range for type: ${option}`);
      }
    }

    return option as RangeSelectOption;
  });

  const commitRange = (range: Date[] | undefined) => {
    committedRangeRef.current = range;
    setDraftRange(range);
    props.onValueChange(range);
    if (range && range.length === 2) {
      op.current?.hide();
    }
  };

  const onCalendarChange = (value: Nullable<(Date | null)[]>) => {
    const range = normalizeRange(value);
    if (commitOnCompleteRange) {
      setDraftRange(range);
      if (range && range.length === 2) {
        commitRange(range);
      }
      return;
    }
    commitRange(range);
  };

  const isOverlayHidden = () => {
    const panel = document.querySelector('.p-overlaypanel');
    return !panel || getComputedStyle(panel as HTMLElement).display === 'none';
  };

  const syncDraftToCommitted = () => {
    setDraftRange(committedRangeRef.current);
  };

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    if (isOverlayHidden()) {
      syncDraftToCommitted();
      op.current?.toggle(event, event.currentTarget);
      suppressClickRef.current = true;
    }
  };

  const handleInputClick = (event: React.MouseEvent<HTMLInputElement>) => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    syncDraftToCommitted();
    op.current?.toggle(event, event.currentTarget);
  };

  const handleOverlayHide = () => {
    // Clear so the first click after selection reopens while the input stays focused.
    suppressClickRef.current = false;
    if (commitOnCompleteRange) {
      const committed = committedRangeRef.current;
      setDraftRange(committed);
    }
  };

  const getDisplay = () => {
    const displayValue = commitOnCompleteRange ? committedRangeRef.current : props.value;
    const match = options.find(
      (option) =>
        displayValue?.length === 2 &&
        option.startDate.getTime() === displayValue[0].getTime() &&
        option.endDate.getTime() === displayValue[1].getTime(),
    );

    if (match) {
      return match.label;
    }

    return displayValue?.map((d) => format(d, 'MM/dd/yy')).join('-');
  };

  const calendarValue = commitOnCompleteRange ? draftRange : props.value;

  return (
    <React.Fragment>
      <span className="font-semibold text-gray-800 text-lg pr-2">{props.label ?? 'Date Range:'}</span>
      <InputText
        ref={inputRef}
        className={classNames('hover:cursor-pointer', props.className)}
        readOnly
        autoComplete="off"
        inputMode="none"
        aria-label="Report date range"
        onClick={handleInputClick}
        onFocus={handleInputFocus}
        value={getDisplay()}
      />
      <OverlayPanel
        ref={op}
        className={styles.overlay}
        onHide={handleOverlayHide}
        onShow={() => {
          requestAnimationFrame(() => op.current?.align());
        }}
      >
        <div className={styles.body}>
          <ul className="py-4 w-[150px]">
            {options.map((option) => {
              const match =
                props.value?.length === 2 &&
                option.startDate.getTime() === props.value[0].getTime() &&
                option.endDate.getTime() === props.value[1].getTime();
              return (
                <li key={option.label}>
                  <button
                    type="button"
                    className={classNames(
                      'px-5 py-2 w-full text-left hover:cursor-pointer hover:bg-[#F9F9F9] hover:text-[#009ef7]',
                      {
                        '!bg-[#009ef7] !text-white': !!match,
                      },
                    )}
                    onClick={() => onCalendarChange([option.startDate, option.endDate])}
                  >
                    {option.label}
                  </button>
                </li>
              );
            })}
          </ul>
          <Calendar
            inline
            selectionMode="range"
            value={calendarValue}
            onChange={(e) => onCalendarChange(e.value)}
            className="max-w-full"
          />
        </div>
      </OverlayPanel>
    </React.Fragment>
  );
};

export default CalendarRange;
